import { clamp01 } from "../math.js";
import type { RippleSource, System, World } from "./types.js";
import { levelFromStrength, waveDurScale } from "./ripple.js";

export interface WingConfig {
  restMin: number;
  restMax: number;
  gapMin: number;
  gapMax: number;
  /** The first gust (the kind of beat that sends a wave) comes at a random time in
      `[firstGustMin, firstGustMax]` seconds, so you don't wait long for the first wave. After
      that, gusts rest `[restMin, restMax]` seconds apart. Soft flutters can show up before the
      first gust. */
  firstGustMin: number;
  firstGustMax: number;
  durMin: number;
  durMax: number;
  ampMin: number;
  ampMax: number;
  lag: number;
  /** Click flap: amplitude, gain, cooldown, and a duration range. Each flap picks a fresh
      duration, so no two look quite the same. */
  clickAmp: number;
  clickDurMin: number;
  clickDurMax: number;
  clickCool: number;
  clickGain: number;
  bigChance: number;
  bigAmp: number;
  bigGain: number;
  bigDur: number;
  /** Flutter: fold amplitude (in beat-amp units) and a duration range, picked fresh each time. */
  flutterAmp: number;
  flutterDurMin: number;
  flutterDurMax: number;
  /** Ambient flutters run on their own clock, one every `[flutterGapMin, flutterGapMax]` seconds,
      separate from the gusts. After each one, the next waits a random
      `[flutterCoolMin, flutterCoolMax]` seconds. */
  flutterGapMin: number;
  flutterGapMax: number;
  flutterCoolMin: number;
  flutterCoolMax: number;
  /** After a click flap, the next flutter waits `[postFlapFlutterMin, postFlapFlutterMax]` seconds
      (longer than the usual cooldown), so it doesn't step on the flap. */
  postFlapFlutterMin: number;
  postFlapFlutterMax: number;
}
/** Clicks per second the butterfly listens to. Faster clicks are ignored: no flap, no wave. At one
    a second, every click gets its own wave, and the waves can overlap (`RippleSystem.canEmit`).
    `clickCool`, the minimum gap between clicks, is `1 / WING_CLICK_RATE`. */
export const WING_CLICK_RATE = 1;

export const WING_DEFAULTS: WingConfig = {
  restMin: 20.0,
  restMax: 30.0,
  gapMin: 0.1,
  gapMax: 0.42,
  firstGustMin: 6,
  firstGustMax: 8,
  durMin: 0.22,
  durMax: 1.1,
  ampMin: 0.02,
  ampMax: 0.26,
  lag: 0.05,
  clickAmp: 0.27,
  clickDurMin: 0.72,
  clickDurMax: 0.78,
  clickCool: 1 / WING_CLICK_RATE,
  clickGain: 1.8,
  bigChance: 0.22,
  bigAmp: 0.54,
  bigGain: 2.2,
  bigDur: 1.55,
  flutterAmp: 0.16,
  flutterDurMin: 0.6,
  flutterDurMax: 1.1,
  flutterGapMin: 4,
  flutterGapMax: 9,
  flutterCoolMin: 3,
  flutterCoolMax: 6,
  postFlapFlutterMin: 5,
  postFlapFlutterMax: 8,
};

/** Flap ↔ wave coupling. A beat's magnitude (`beatAmp · beatGain`) becomes one normalized
    strength, which sets the flap's speed and the wave's reach and speed. The raw magnitude
    still sets how deep the distortion goes. One beat, one intensity. */
const WING_FLAP_REF = 1.1;
const WAVE_AMP_FLOOR = 0.35;
const WAVE_AMP_SCALE = 3.6;
const WING_CLICK_STRENGTH_MIN = 0.55;
const WING_CLICK_STRENGTH_MAX = 0.8;

/** Clicks vary a little, but stay in the middle of the strength range. */
export function randomClickStrength(rng: () => number): number {
  const t = clamp01(rng());
  return WING_CLICK_STRENGTH_MIN + (WING_CLICK_STRENGTH_MAX - WING_CLICK_STRENGTH_MIN) * t;
}

const FULL_STRENGTH_DURATION_SCALE = waveDurScale(1);

function clampStrength(strength: number): number {
  return clamp01(strength);
}

/** Strength 1 keeps the base timing. Weaker strokes slow down on the same curve as their
    waves. */
function flapDurationScale(strength: number): number {
  return waveDurScale(clampStrength(strength)) / FULL_STRENGTH_DURATION_SCALE;
}

/** A beat is an ambient `gust`, a deliberate `flap`, or a `flutter`. */
export type BeatKind = "gust" | "flap" | "flutter";

/** What the wing is doing right now. */
export interface WingStats {
  beatStart: number;
  /** Mid-beat. */
  beatOn: boolean;
  kind: BeatKind;
  /** The normalized strength that this beat's wave shares. */
  strength: number;
  /** This beat's duration, in seconds (randomized). */
  beatDur: number;
}

export class WingSystem implements System {
  private beatOn = false;
  private beatStart = 0;
  private beatDur = 0;
  private beatAmp = 0;
  private beatGain = 1;
  private beatStrength = 0;
  private beatKind: BeatKind = "gust";
  // Whether this beat is part of the gust rhythm (gusts, their follow-up bursts, and
  // deliberate flaps). If it is, its end schedules the next gust. Flutters keep their own timer.
  private rhythmBeat = false;
  private burstLeft = 0;
  private tNext: number;
  // Until the first gust has gone, a visual flap (the WebGPU handoff's) doesn't push it back.
  private firstGustDue = true;
  // After a deliberate flap, natural beats hold off until this time.
  private quietUntil = 0;
  // When the next ambient flutter is due.
  private flutterNext: number;
  private lastClick = -9;
  private flutterReadyAt = 0; // earliest the next flutter can start (random cooldown after each)

  constructor(
    private readonly cfg: WingConfig = WING_DEFAULTS,
    private readonly rng: () => number = Math.random,
    private readonly emit: (
      now: number,
      amp: number,
      strength: number,
      source: RippleSource,
      level: number,
    ) => void = () => {},
    // Whether a beat's wave can start now (`RippleSystem.canEmit`).
    private readonly canRipple: () => boolean = () => true,
  ) {
    this.flutterNext = cfg.flutterGapMin;
    this.tNext = this.rnd(cfg.firstGustMin, cfg.firstGustMax);
  }

  private rnd(a: number, b: number): number {
    return a + this.rng() * (b - a);
  }
  /** A click flap at an explicit effect `level`. The click-gesture tracker counts every click;
      this cooldown just keeps accepted clicks from restarting a flap that's still showing. */
  flap(
    now: number,
    level = 3,
    strength?: number,
    speed = 1,
    source: "click" | "theme" = "click",
  ): boolean {
    const c = this.cfg;
    if (now - this.lastClick < c.clickCool) return false;
    this.lastClick = now;
    // A deliberate flap gets no follow-up burst, and the next natural beat waits the longest
    // rest, so an ambient wave doesn't pile on right after the click's.
    this.burstLeft = 0;
    this.quietUntil = now + c.restMax;
    // Hold off flutters for a bit, so one doesn't step on the flap.
    this.flutterReadyAt = now + this.rnd(c.postFlapFlutterMin, c.postFlapFlutterMax);
    this.rhythmBeat = true;
    // A flap always sends a wave. If the wave can't start, the click gets a flutter instead.
    if (!this.canRipple()) {
      this.startSilentFlutter(now);
      return true;
    }
    this.beatOn = true;
    this.beatStart = now;
    this.beatKind = "flap";
    this.beatGain = c.clickGain;
    if (strength === undefined) {
      this.beatAmp = c.clickAmp;
      this.beatStrength = this.strengthFromMagnitude();
    } else {
      this.beatStrength = clampStrength(strength);
      this.beatAmp = (this.beatStrength * WING_FLAP_REF) / c.clickGain;
    }
    this.beatDur =
      (this.rnd(c.clickDurMin, c.clickDurMax) * flapDurationScale(this.beatStrength)) /
      Math.max(0.1, speed);
    // A click is a firm flap, and its wave's reach and depth scale with it (`emitBeat`).
    this.emitBeat(now, source, level);
    return true;
  }

  /** A flutter: a quick, shallow, sustained beat. Refused while another beat is going, and at
      most one per `flutterCool` seconds. Returns whether it started. Flutters only move the wings;
      gusts are what send waves. */
  flutter(now: number): boolean {
    const c = this.cfg;
    if (this.beatOn || now < this.flutterReadyAt) return false;
    // A new random cooldown each time.
    this.flutterReadyAt = now + this.rnd(c.flutterCoolMin, c.flutterCoolMax);
    this.rhythmBeat = false;
    this.beatOn = true;
    this.beatStart = now;
    this.beatKind = "flutter";
    this.beatAmp = c.flutterAmp;
    this.beatGain = 1;
    this.beatStrength = this.strengthFromMagnitude();
    this.beatDur =
      this.rnd(c.flutterDurMin, c.flutterDurMax) * flapDurationScale(this.beatStrength);
    return true;
  }

  /** A one-off flap at `strength`, with no wave. It pushes back the next flutter, and every
      gust after the first, so nothing crowds it. */
  visualFlap(now: number, strength: number): void {
    const c = this.cfg;
    this.rhythmBeat = !this.firstGustDue;
    this.beatOn = true;
    this.beatStart = now;
    this.beatKind = "flap";
    this.beatStrength = clampStrength(strength);
    this.beatDur = this.rnd(c.clickDurMin, c.clickDurMax) * flapDurationScale(this.beatStrength);
    this.beatAmp = (this.beatStrength * WING_FLAP_REF) / c.clickGain;
    this.beatGain = c.clickGain;
    this.burstLeft = 0;
    this.flutterReadyAt = now + this.rnd(c.postFlapFlutterMin, c.postFlapFlutterMax);
    if (this.rhythmBeat) this.quietUntil = now + c.restMax;
  }

  /** A flutter with no wave, just the wings. */
  private startSilentFlutter(now: number): void {
    const c = this.cfg;
    this.beatOn = true;
    this.beatStart = now;
    this.beatKind = "flutter";
    this.beatAmp = c.flutterAmp;
    this.beatGain = 1;
    this.beatStrength = this.strengthFromMagnitude();
    this.beatDur =
      this.rnd(c.flutterDurMin, c.flutterDurMax) * flapDurationScale(this.beatStrength);
  }

  /** A copy of the wing's current state. */
  stats(): WingStats {
    return {
      beatStart: this.beatStart,
      beatOn: this.beatOn,
      kind: this.beatKind,
      strength: this.beatStrength,
      beatDur: this.beatDur,
    };
  }

  update(world: World): void {
    const c = this.cfg;
    const now = world.time;

    // Flutters run on their own, much shorter rhythm, separate from the gusts' rest.
    if (!this.beatOn && now >= this.flutterNext) {
      this.flutter(now);
      this.flutterNext = now + this.rnd(c.flutterGapMin, c.flutterGapMax);
    }

    if (!this.beatOn && now >= this.tNext && now >= this.quietUntil) {
      if (!this.canRipple()) {
        // A gust always sends a wave. If one can't start, a follow-up gust in a burst plays as
        // a flutter, and a fresh gust waits a moment for the waves to clear.
        if (this.burstLeft > 0) {
          this.startSilentFlutter(now);
          this.rhythmBeat = true;
          this.burstLeft--;
        } else this.tNext = now + this.rnd(c.gapMin, c.gapMax);
      } else {
        this.firstGustDue = false;
        this.rhythmBeat = true;
        this.beatOn = true;
        this.beatStart = now;
        this.beatKind = "gust";
        const big = this.rng() < c.bigChance;
        const baseDuration = big ? this.rnd(c.durMax, c.bigDur) : this.rnd(c.durMin, c.durMax);
        this.beatAmp = big ? this.rnd(c.ampMax, c.bigAmp) : this.rnd(c.ampMin, c.ampMax);
        this.beatGain = big ? c.bigGain : 1;
        this.beatStrength = this.strengthFromMagnitude();
        this.beatDur = baseDuration * flapDurationScale(this.beatStrength);
        // The wave's reach and depth scale with how hard the wing actually flaps (`emitBeat`): a
        // soft beat makes a soft wave, and a big beat a big one.
        this.emitBeat(now, "ambient");
        if (this.burstLeft <= 0) {
          const t = this.rng();
          this.burstLeft =
            t < 0.45
              ? 1
              : t < 0.8
                ? 2 + Math.floor(this.rng() * 2)
                : 4 + Math.floor(this.rng() * 3);
        }
        this.burstLeft--;
      }
    }
    if (this.beatOn && now >= this.beatStart + this.beatDur + c.lag) {
      this.beatOn = false;
      if (this.rhythmBeat) {
        const wait =
          this.burstLeft > 0 ? this.rnd(c.gapMin, c.gapMax) : this.rnd(c.restMin, c.restMax);
        this.tNext = Math.max(now + wait, this.quietUntil);
      }
    }
  }

  /** Send the current beat's wave, at the same strength as the wing's motion. */
  private emitBeat(now: number, source: RippleSource, level?: number): void {
    const m = this.beatAmp * this.beatGain; // how far the wing moves, roughly (fold amp × gain)
    const s = this.beatStrength;
    // A click brings its own level (from the click-gesture rate). Ambient beats get theirs from
    // how hard they flap.
    this.emit(now, WAVE_AMP_FLOOR + m * WAVE_AMP_SCALE, s, source, level ?? levelFromStrength(s));
  }

  private strengthFromMagnitude(): number {
    return clampStrength((this.beatAmp * this.beatGain) / WING_FLAP_REF);
  }
}
